import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropdownItem, type ITooltipProps, toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  EnumUserRoles,
  type Permissions,
  type IPermissionsContactAccess,
  getAssignableRoleItems,
} from 'entities/Users'
import { type AccessRequest } from 'entities/Users/model/AccessRequest'
import {
  permissionsConfig,
  type PermissionGroupConfig,
  type IPermissions as IOrganizationPermissions,
  checkIsCustomPermission,
} from 'entities/Permissions'
import { UsersApi } from 'entities/Users/api/users'
import { upgradeMemberAction } from 'pages/settings/pages/members/ui/actions/upgradeMember/upgradeMember'
import { downgradeMemberAction } from 'pages/settings/pages/members/ui/actions/downgradeMember/downgradeMember'
import { RejectAccessRequestStore } from 'pages/settings/pages/members/pages/membersView/ui/RequestsMembersTab/ui/RejectAccessRequestModal/store/rejectAccessRequestStore'
import { ReviewPermissionsModalContent } from '../ui/ReviewPermissionsModalContent'

type PermissionDisplayItem = {
  key: keyof Permissions
  label: string
  value: boolean | IPermissionsContactAccess
  disabled: boolean
  custom: boolean
  valueType: 'boolean' | 'contactAccess'
  contactAccessValue?: IPermissionsContactAccess
  tooltip?: {
    label: React.ReactNode
    tooltipProps?: ITooltipProps
  }
}

type PermissionDisplayGroup = {
  key: string
  label: string
  permissions: PermissionDisplayItem[]
}

export class ReviewPermissionsStore {
  private readonly _modalId = nanoid()
  private _accessRequest: AccessRequest | null = null
  private _loading = false
  private _initLoading = false
  private _useOrganizationSettings = false
  private _permissions: Permissions | null = null
  private _role: EnumUserRoles | null = null

  constructor(readonly entities: IEntitiesStore, readonly onSuccess?: () => void) {
    makeAutoObservable(this)
  }

  openModal = async (accessRequest: AccessRequest) => {
    this._accessRequest = accessRequest
    this._initLoading = true

    modalStore.addModal({
      id: this._modalId,
      title: 'Permissions update',
      pureContent: true,
      width: 480,
      ModalContent: ReviewPermissionsModalContent,
      ModalContentProps: { store: this },
    })

    try {
      await this.entities.permissionsStore.init()

      runInAction(() => {
        const { role, permissions, useOrganizationSettings } = accessRequest.user

        this._role = role
        this._useOrganizationSettings = useOrganizationSettings

        if (permissions) {
          this._permissions = permissions
        } else {
          this._permissions = this.getDefaultPermissions(role)
        }

        this._initLoading = false
      })
    } catch {
      runInAction(() => {
        this._initLoading = false
      })
    }
  }

  closeModal = () => {
    modalStore.removeModal(this._modalId)
    this._reset()
  }

  openRejectModal = () => {
    if (!this._accessRequest) return

    const accessRequest = this._accessRequest
    const onSuccess = this.onSuccess

    this.closeModal()

    const rejectStore = new RejectAccessRequestStore(() => {
      onSuccess?.()
    })
    rejectStore.openModal(accessRequest)
  }

  onUpdate = async () => {
    if (!this._accessRequest || !this._role || !this._permissions) return
    const userEntity = this.entities.usersStore.getItem(this._accessRequest.user.id)

    if ((this.isDowngrade || this.isUpgrade) && !userEntity) {
      toastStore.add({
        type: 'error',
        title: 'User not found',
      })

      return
    }

    if (this.isDowngrade && userEntity) {
      const permissions = this._permissions
      const useOrganizationSettings = this._useOrganizationSettings
      const onSuccess = this.onSuccess

      this.closeModal()

      downgradeMemberAction(userEntity, this.entities, permissions, useOrganizationSettings, () => {
        onSuccess?.()
      })

      return
    }

    if (this.isUpgrade && userEntity) {
      const role = this._role
      const permissions = this._permissions
      const useOrganizationSettings = this._useOrganizationSettings
      const onSuccess = this.onSuccess

      this.closeModal()

      upgradeMemberAction(
        userEntity,
        this.entities,
        {
          role: role,
          permissions: permissions,
          use_organization_settings: useOrganizationSettings,
        },
        () => {
          onSuccess?.()
        }
      )

      return
    }

    try {
      runInAction(() => {
        this._loading = true
      })

      await UsersApi.updateMember(this._accessRequest.user.id, {
        role: this._role,
        permissions: this._permissions,
        use_organization_settings: this._useOrganizationSettings,
      })

      this.closeModal()

      toastStore.add({
        type: 'success',
        title: 'Permissions updated',
      })

      this.onSuccess?.()
    } catch (error) {
      toastStore.add({
        type: 'error',
        title: 'Failed to update permissions',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _getOrganizationPermissions = (role: EnumUserRoles): IOrganizationPermissions | null => {
    return this.entities.permissionsStore.permissions?.[role]?.organization_permissions ?? null
  }

  isCustomPermission = (
    key: keyof Permissions,
    contactAccessValue?: IPermissionsContactAccess | null
  ): boolean => {
    if (!this._role || !this._permissions) return false

    const rolePermissionsData = this.entities.permissionsStore.permissions?.[this._role] ?? null

    return checkIsCustomPermission(key, this._permissions, rolePermissionsData, contactAccessValue)
  }

  isUnavailablePermission = (key: keyof Permissions) => {
    if (!this._role || !this._permissions) return false
    return this.unavailablePermissions?.find((permission) => permission.key === key)
  }

  getDefaultPermissions = (role: EnumUserRoles): Permissions | null => {
    const organizationPermissions = this._getOrganizationPermissions(role)

    if (!organizationPermissions) return null

    return {
      billing: organizationPermissions.billing.default,
      broadcasts: organizationPermissions.broadcasts.default,
      campaigns: organizationPermissions.campaigns.default,
      contactAccess: organizationPermissions.contactAccess.default,
      contactCreate: organizationPermissions.contactCreate.default,
      dataAndSecurity: organizationPermissions.dataAndSecurity.default,
      inbox: organizationPermissions.inbox.default,
      manualOptIn: organizationPermissions.manualOptIn.default,
      members: organizationPermissions.members.default,
      phoneChecker: organizationPermissions.phoneChecker.default,
      reportingAndAnalytics: organizationPermissions.reportingAndAnalytics.default,
      textBot: organizationPermissions.textBot.default,
      triggers: organizationPermissions.triggers.default,
      reviews: organizationPermissions.reviews?.default ?? false,
      surveys: organizationPermissions.surveys?.default ?? false,
      transferConversation: organizationPermissions.transferConversation.default,
      cannedMessage: organizationPermissions.cannedMessage.default,
      reassignConversation: organizationPermissions.reassignConversation.default,
    }
  }

  setRole = (role: EnumUserRoles) => {
    this._role = role
    this._useOrganizationSettings = true

    const defaultPermissions = this.getDefaultPermissions(role)
    if (defaultPermissions) {
      this._permissions = defaultPermissions
    }
  }

  setUseOrganizationSettings = (value: boolean) => {
    this._useOrganizationSettings = value

    if (!this._role) return

    if (value) {
      const defaultPermissions = this.getDefaultPermissions(this._role)
      if (defaultPermissions) {
        this._permissions = defaultPermissions
      }
    }
  }

  setPermission = (key: string, value: boolean | IPermissionsContactAccess) => {
    if (!this._role || !this._permissions) return

    this._permissions = {
      ...this._permissions,
      [key]: value,
    }
  }

  get permissionsArray(): PermissionDisplayGroup[] {
    const permissions = this._permissions
    if (!permissions) return []

    return permissionsConfig.map((group: PermissionGroupConfig) => ({
      key: group.key,
      label: group.label,
      permissions: group.permissions.map((permission) => {
        const isContactAccess = permission.valueType === 'contactAccess'
        const isUnavailable = this.isUnavailablePermission(permission.key)

        let value: boolean | IPermissionsContactAccess
        if (isUnavailable) {
          value = false
        } else if (isContactAccess && permission.contactAccessValue) {
          value = permission.contactAccessValue
        } else {
          value = permissions[permission.key]
        }

        return {
          key: permission.key,
          label: permission.label,
          value: value,
          disabled: !!this.isUnavailablePermission(permission.key),
          custom: this.isCustomPermission(
            permission.key,
            isContactAccess ? permission.contactAccessValue : null
          ),
          valueType: permission.valueType,
          contactAccessValue: permission.contactAccessValue,
          ...(permission?.tooltip ? { tooltip: permission.tooltip } : {}),
        }
      }),
    }))
  }

  get accessRequest() {
    return this._accessRequest
  }

  get isDowngrade(): boolean {
    if (!this._accessRequest || !this._role) return false

    return (
      this._role === EnumUserRoles.VIEW_ONLY &&
      this._accessRequest.user.role !== EnumUserRoles.VIEW_ONLY
    )
  }

  get isUpgrade(): boolean {
    if (!this._accessRequest || !this._role) return false

    return (
      this._role !== EnumUserRoles.VIEW_ONLY &&
      this._accessRequest.user.role === EnumUserRoles.VIEW_ONLY &&
      !this.entities.subscriptionStore.seatsAvailable
    )
  }

  get submitButtonText(): string {
    return this.isDowngrade || this.isUpgrade ? 'Next' : 'Update'
  }

  get loading() {
    return this._loading
  }

  get initLoading() {
    return this._initLoading
  }

  get role() {
    return this._role
  }

  get useOrganizationSettings() {
    return this._useOrganizationSettings
  }

  get roleItems(): IDropdownItem[] {
    return getAssignableRoleItems(
      this.entities.usersStore.user?.role,
      this._accessRequest?.user.role
    )
  }

  get contactAccessValue(): IPermissionsContactAccess | null {
    return this._permissions?.contactAccess ?? null
  }

  get unavailablePermissions() {
    if (!this._role) return null
    return this.entities.permissionsStore.permissions?.[this._role]?.unavailable_permissions ?? null
  }

  private _reset = () => {
    this._accessRequest = null
    this._permissions = null
    this._role = null
    this._useOrganizationSettings = false
    this._loading = false
    this._initLoading = false
  }
}
